import { useMemo } from 'react'

import { RECENT_DAYS, partOfTheDay } from '@gymcloud/nutrition/order'
import {
  placed,
  type Asking,
  type Logged,
  type MealStanding,
  type PlacingInput,
  type Standing,
} from '@gymcloud/nutrition/suggestions'

import type { MealsAnswer } from '~/api/endpoints'
import { useDataState } from '~/data/data-context'
import { todayKey } from '~/data/weighins'
import { byNewestUpdate } from '~/data/sync'
import {
  foodKey,
  isOnlyBroughtIn,
  membersOn,
  nowTime,
  type Entry,
  type Food,
  type Meal,
  type MealLog,
  type RecentFood,
} from '~/data/nutrition'

/**
 * The foods and the meals, read off the mirror the change feed fills. Every one of these was
 * a list route of its own; the account holds them all, so they are worked out here instead —
 * which is also what makes a food saved in one place stand in the picker in the same breath.
 *
 * Each is null until the feed has been pulled at least once: a picker that said "no foods of
 * your own yet" on the strength of an unread account would be saying something false.
 */

/**
 * The moment a list is being read at. Wrapped so the rule that keeps a render pure can see it
 * is not one: what a list is worth depends on when it is read, which is the whole point of
 * `/specs/features/food-order.spec.md`.
 */
function readingNow(): number {
  return Date.now()
}

/** Newest first, by the moment it was marked. */
function byNewestMark<T extends { createdAt: number }>(a: T, b: T): number {
  return b.createdAt - a.createdAt
}

/** The account's own foods and recipes. */
export function useMyFoods(): Food[] | null {
  return useDataState().foods
}

/** The foods and recipes marked as favourites, newest first. */
export function useFavouriteFoods(): Food[] | null {
  const held = useDataState().favourites
  return useMemo(
    () => (held === null ? null : [...held].sort(byNewestMark).map((each) => each.food)),
    [held],
  )
}

/** The day ninety days back from today, as the diary writes a day. */
function windowOpensOn(now: number): string {
  const opens = new Date(now)
  opens.setDate(opens.getDate() - RECENT_DAYS)
  const month = `${opens.getMonth() + 1}`.padStart(2, '0')
  const day = `${opens.getDate()}`.padStart(2, '0')
  return `${opens.getFullYear()}-${month}-${day}`
}

/**
 * The foods logged before: one per name, with the amount it was last logged in, how many times
 * it has gone out over the window the order is read across, and how many of those times fell
 * in each part of the day — `/specs/features/food-order.spec.md`.
 *
 * The window is the last ninety days, which is also all the Android app holds: the order is
 * worked out by each app for itself, so both have to count over the same days or one account
 * is offered two different lists.
 */
export function useRecentFoods(): RecentFood[] | null {
  const entries = useDataState().entries
  return useMemo(() => {
    if (entries === null) {
      return null
    }
    const since = windowOpensOn(readingNow())
    /*
     * One row per food rather than per name: two foods called the same thing — an own "Oats"
     * and the library's — are two foods, and collapsing them hid one of them from a favourite
     * looking itself up by the food it is. The Android store keys this the same way.
     */
    const byName = new Map<string, RecentFood>()
    for (const entry of [...entries].sort((a, b) => -byNewestUpdate(a, b))) {
      if (entry.day < since) {
        continue
      }
      /*
       * An entry logged through a meal does not put its food here: the meal stands in the list
       * as itself instead of its members flooding it. Nor does one brought in from another
       * app, which is offered nowhere a food is picked.
       */
      if (entry.food === null || isOnlyBroughtIn(entry.food) || entry.fromMeal === true) {
        continue
      }
      const key = foodKey(entry.food)
      const held = byName.get(key)
      /*
       * The part is taken from the time of day the entry stands at and not from the moment it
       * was written: an entry says what time it was eaten at, and that is the question being
       * asked. The moment is the later of the two, which is when it was last logged.
       */
      const part = partOfTheDay(...clockOf(entry.time))
      byName.set(key, {
        food: entry.food,
        // Oldest first, so the last entry seen is the newest: its amount is the one the
        // food was last logged in, which is what the list offers.
        amount: entry.amount,
        times: (held?.times ?? 0) + 1,
        lastAt: Math.max(held?.lastAt ?? 0, Date.parse(entry.updatedAt)),
        inParts: {
          ...held?.inParts,
          [part]: (held === undefined ? 0 : (held.inParts[part] ?? 0)) + 1,
        },
      })
    }
    /*
     * Most recently logged first, which is only the order this list is gathered in: what it
     * stands in is what each thing is worth now, and the callers place it with everything else
     * they are offering beside it — see [placedRecents].
     */
    return [...byName.values()].sort((a, b) => b.lastAt - a.lastAt)
  }, [entries])
}

/** The hour and the minute of a time of day as the diary writes it — "08:20". */
function clockOf(time: string): [number, number] {
  const [hour, minute] = time.split(':')
  return [Number(hour) || 0, Number(minute) || 0]
}

/**
 * When a thing was last written, as a moment — for something never logged, this is what places
 * it: `/specs/features/food-order.spec.md`. A row that says nothing has no moment at all.
 */
export function madeAt(updatedAt: string | undefined): number | null {
  if (updatedAt === undefined || updatedAt === '') {
    return null
  }
  const at = Date.parse(updatedAt)
  // Above the epoch, as the Kotlin port reads it: a moment of nought is a row saying nothing.
  return Number.isNaN(at) || at <= 0 ? null : at
}

/**
 * Every list the account logs out of is placed by one reckoning. See
 * `/specs/features/food-order.spec.md`: the readings first, what a thing is worth on its own
 * after them.
 *
 * What a list is placed off, the asking included. Made once as the list opens and held, since
 * a list is placed for the moment it was opened at and not afresh as the clock moves.
 */
export type Placing = PlacingInput<Entry>

/**
 * One entry as the reckoning reads it.
 *
 * A quick entry has no key and is never a thing, and neither is a food brought in from another
 * app with no row of its own: it is offered nowhere a food is picked, so it is placed in no
 * list — the Android port reads an entry the same way.
 */
export function loggedOf(entry: Entry): Logged {
  return {
    key: entry.food === null || isOnlyBroughtIn(entry.food) ? null : foodKey(entry.food),
    name: entry.name,
    day: entry.day,
    time: entry.time,
    loggedAt: entry.createdAt,
    mealId: entry.mealId ?? null,
    fromMeal: entry.fromMeal === true,
  }
}

/** Logging onto a day at a time on it, which is what most of the lists are placed for. */
export function askingToLog(day: string, time: string, now: number = readingNow()): Asking {
  return { day, time, now, purpose: { kind: 'log' } }
}

/**
 * A form asking for itself — a recipe being made, a meal being composed. It holds what it
 * already has, which is its plate: the companion reads off that, and nothing about the time of
 * day moves the list.
 */
export function askingForForm(plateKeys: readonly string[], now: number = readingNow()): Asking {
  return {
    day: todayKey(new Date(now)),
    time: nowTime(new Date(now)),
    purpose: { kind: 'form', plateKeys },
    now,
  }
}

/**
 * The asking a form's own picker is placed for, held while what the form holds does not
 * change: a list is placed as it opens and stands in that order until it is opened again.
 */
export function useAskingForForm(plateKeys: readonly string[]): Asking {
  const keys = plateKeys.join('\u0000')
  return useMemo(() => askingForForm(keys === '' ? [] : keys.split('\u0000')), [keys])
}

/**
 * Everything one asking is answered off. Null until the log, the days, the workouts and the
 * meals have all arrived: a list placed off part of the log would stand in one order and then
 * re-sort itself, which is settled data before settled UI.
 */
export function usePlacing(asking: Asking): Placing | null {
  const off = usePlacingOff()
  return useMemo(() => (off === null ? null : { ...off, asking }), [off, asking])
}

/** Everything a reckoning reads, the asking apart. */
type PlacingOff = Omit<Placing, 'asking'>

/** The log, the days, the workouts and the meals, or null until they have all arrived. */
function usePlacingOff(): PlacingOff | null {
  const { entries, days, workouts, synced, timelineSettled, mealLogs } = useDataState()
  const meals = useMyMeals()
  return useMemo(() => {
    if (
      !synced ||
      entries === null ||
      days === null ||
      !timelineSettled ||
      meals === null ||
      mealLogs === null
    ) {
      return null
    }
    return {
      entries,
      of: loggedOf,
      incompleteDays: days.filter((each) => each.incomplete).map((each) => each.day),
      /*
       * Every finished workout the account has, whether or not the food log shows workouts:
       * after training is read off what was trained and not off what is drawn.
       */
      workouts: workouts.flatMap((workout) =>
        workout.finishedAt === null ? [] : [{ finishedAt: workout.finishedAt }],
      ),
      meals: meals.meals.map((meal) => ({ id: meal.id })),
    }
  }, [entries, days, workouts, synced, timelineSettled, meals, mealLogs])
}

/** A meal as the reckoning reads it: its logs for its part of the day, its count for how often. */
function standingOfMeal(meal: Meal, logs: readonly MealLog[]): MealStanding {
  return {
    kind: 'meal',
    id: meal.id,
    name: meal.name,
    madeAt: madeAt(meal.updatedAt),
    logCount: Math.max(meal.logCount, logs.length),
    logs: logs.map((log) => ({ loggedAt: log.loggedAt })),
    lastLoggedAt: meal.lastLoggedAt,
  }
}

/** The logs of each meal, by the meal they are of. */
function logsByMeal(logs: readonly MealLog[]): Map<string, MealLog[]> {
  const logsOf = new Map<string, MealLog[]>()
  for (const log of logs) {
    if (log.mealId === null) {
      continue
    }
    logsOf.set(log.mealId, [...(logsOf.get(log.mealId) ?? []), log])
  }
  return logsOf
}

/** One thing standing among the things logged before, whatever kind of thing it is. */
export type PlacedRow =
  { kind: 'food'; each: RecentFood } | { kind: 'meal'; meal: Meal } | { kind: 'log'; log: MealLog }

/**
 * The foods logged before, the meals logged among them and what was logged without being kept,
 * as one list, placed by the one reckoning — `/specs/features/food-order.spec.md`. A food that
 * has come to be logged as a meal stands as that meal, which is `placed` dropping its row.
 */
export function placedRecents(
  recent: readonly RecentFood[],
  meals: readonly Meal[],
  logs: readonly MealLog[],
  unkept: readonly MealLog[],
  placing: Placing,
): PlacedRow[] {
  const logsOf = logsByMeal(logs)
  const rows: PlacedRow[] = [
    ...recent.map((each): PlacedRow => ({ kind: 'food', each })),
    ...meals
      .filter((meal) => meal.lastLoggedAt !== null)
      .map((meal): PlacedRow => ({ kind: 'meal', meal })),
    ...unkept.map((log): PlacedRow => ({ kind: 'log', log })),
  ]
  return placed(
    rows,
    (row): Standing => {
      if (row.kind === 'meal') {
        return standingOfMeal(row.meal, logsOf.get(row.meal.id) ?? row.meal.logs)
      }
      if (row.kind === 'log') {
        return { kind: 'unkept', id: row.log.id, name: row.log.name, loggedAt: row.log.loggedAt }
      }
      // A food logged before is worth what its entries say; nothing is read off its row.
      return { kind: 'food', key: foodKey(row.each.food), name: row.each.food.name, madeAt: null }
    },
    placing,
  ).map((each) => each.row)
}

/**
 * The account's own foods, or its recipes, placed the way everything else it logs from is.
 * One never logged is placed by when it was made, so a food made a moment ago is near the head
 * rather than at the foot of three hundred.
 */
export function placedOwn(foods: readonly Food[], placing: Placing): Food[] {
  return placed(
    foods,
    (food): Standing => ({
      kind: 'food',
      key: foodKey(food),
      name: food.name,
      madeAt: madeAt(food.updatedAt),
    }),
    placing,
  ).map((each) => each.row)
}

/** The account's meals, placed the same way. */
export function placedMeals(
  meals: readonly Meal[],
  logs: readonly MealLog[],
  placing: Placing,
): Meal[] {
  const logsOf = logsByMeal(logs)
  return placed(
    meals,
    (meal): Standing => standingOfMeal(meal, logsOf.get(meal.id) ?? meal.logs),
    placing,
  ).map((each) => each.row)
}

/** One favourite, whichever kind it is — a favourite may never have been logged. */
export type FavouriteRow = { kind: 'food'; food: Food } | { kind: 'meal'; meal: Meal }

/**
 * The favourites, the foods and the meals in one list, placed the way the foods logged before
 * are placed, and one never logged last — `/specs/features/food-order.spec.md`.
 */
export function placedFavourites(
  favourites: readonly Food[],
  meals: readonly Meal[],
  logs: readonly MealLog[],
  placing: Placing,
): FavouriteRow[] {
  const logsOf = logsByMeal(logs)
  const rows: FavouriteRow[] = [
    ...favourites.map((food): FavouriteRow => ({ kind: 'food', food })),
    ...meals.map((meal): FavouriteRow => ({ kind: 'meal', meal })),
  ]
  return placed(
    rows,
    (row): Standing =>
      row.kind === 'meal'
        ? standingOfMeal(row.meal, logsOf.get(row.meal.id) ?? row.meal.logs)
        : // One never logged is placed by when it was made, as everything never logged is.
          {
            kind: 'food',
            key: foodKey(row.food),
            name: row.food.name,
            madeAt: madeAt(row.food.updatedAt),
          },
    placing,
  ).map((each) => each.row)
}

/** The account's meals, and the logs of what was logged without being kept. */
export function useMyMeals(): MealsAnswer | null {
  const { meals, mealLogs } = useDataState()
  return useMemo(() => {
    if (meals === null || mealLogs === null) {
      return null
    }
    return {
      meals: meals.map((meal) => ({ ...meal, members: membersOn(meal.slots, meal.state) })),
      unsaved: mealLogs
        .filter((log) => log.mealId === null)
        .sort((a, b) => b.loggedAt - a.loggedAt),
    }
  }, [meals, mealLogs])
}

/** The meals marked as favourites, newest first, each the copy taken when it was marked. */
export function useFavouriteMeals(): Meal[] | null {
  const held = useDataState().mealFavourites
  return useMemo(
    () => (held === null ? null : [...held].sort(byNewestMark).map((each) => each.meal)),
    [held],
  )
}

/** Every log of one meal, newest first — the whole of them, the account holding them all. */
export function useMealLogs(mealId: string): MealLog[] | null {
  const held = useDataState().mealLogs
  return useMemo(
    () =>
      held === null
        ? null
        : held.filter((log) => log.mealId === mealId).sort((a, b) => b.loggedAt - a.loggedAt),
    [held, mealId],
  )
}

/** The entries logged with one food, newest first — what a food's page reads its history from. */
export function entriesOfFood(entries: readonly Entry[], key: string): Entry[] {
  return entries
    .filter(
      (entry) =>
        entry.food !== null &&
        (entry.food.id ?? `library:${entry.food.libraryCode ?? entry.food.name}`) === key,
    )
    .sort(byNewestUpdate)
}
